import {
	buildAtlAttributionHeaders,
	positionJobFamiliesQuery,
	type PositionJobFamily,
} from '@atlassian/search-client';

import { type CloudConfig } from '../../../../../common/constants/filters/cloud-config/types';
import {
	type FetchedSelectFilterOption,
	type SelectFilterState,
} from '../../../../../common/constants/filters/select-filter/types';
import { ProductKeys, type ProductKeys1P } from '../../../../../common/constants/products';
import { createMultiSelectFilter } from '../../../../filters/multi-select-filter';

// Number of results returned in dropdown
const LIMIT = 20;

const mapPositionJobFamilyResult = (
	positionJobFamily: PositionJobFamily,
	product: ProductKeys,
): FetchedSelectFilterOption => {
	const encodedValue = encodeURIComponent(positionJobFamily.node.stringValue);
	return {
		trackingKey: encodedValue || '',
		label: positionJobFamily.node.stringValue || '',
		value: encodedValue || '',
		queryParamValue: encodedValue || '',
		product: product,
	};
};

/**
 * Queries position job families by search term
 * @param query
 * @param config
 * @returns
 */
const searchPositionJobFamilies = async (
	query: string,
	config: CloudConfig,
): Promise<FetchedSelectFilterOption[]> => {
	const rqlQuery = `jobFamily ~ '${query}'`;
	const headers = buildAtlAttributionHeaders({
		cloudId: config.cloudId,
		activationId: config.activationId,
		product: config.primaryProduct,
	});
	const positionJobFamiliesResponse = await positionJobFamiliesQuery(
		config.cloudId || '',
		LIMIT,
		rqlQuery,
		undefined,
		headers,
	);
	const positionJobFamilies: PositionJobFamily[] =
		positionJobFamiliesResponse.data?.radar_fieldValues.edges ?? [];

	return positionJobFamilies.map((positionJobFamily) =>
		mapPositionJobFamilyResult(positionJobFamily, config.product as ProductKeys),
	);
};

/**
 * Queries all selected job families by id from query params
 * @param queryParams
 * @param config
 * @returns
 */
const getOptionsWithSelectionsFromQueryParams = async (
	queryParams: string[],
	config: CloudConfig,
): Promise<FetchedSelectFilterOption[]> => {
	const rqlQuery = `jobFamily in ('${queryParams.map((param) => decodeURIComponent(param)).join("', '")}')`;

	const headers = buildAtlAttributionHeaders({
		cloudId: config.cloudId,
		activationId: config.activationId,
		product: config.primaryProduct,
	});
	const positionJobFamiliesResponse = await positionJobFamiliesQuery(
		config.cloudId || '',
		LIMIT,
		rqlQuery,
		undefined,
		headers,
	);
	const positionJobFamilies: PositionJobFamily[] =
		positionJobFamiliesResponse.data?.radar_fieldValues.edges ?? [];

	return positionJobFamilies.map((positionJobFamily) =>
		mapPositionJobFamilyResult(positionJobFamily, config.product as ProductKeys),
	);
};

const productKeys1P: ProductKeys1P[] = [ProductKeys.Talent];

export const filter: Readonly<{
	id: 'positionJobFamily';
	type: 'single-select' | 'multi-select';
	queryParams: {
		key: string;
		getState: (
			queryParams: Record<string, string[]>,
			cloudId: string,
		) => SelectFilterState<'positionJobFamily'>;
	};
	products: ProductKeys[];
	universal: boolean;
	customValueFields?: string[];
	withState: (
		state: unknown,
		config: CloudConfig,
	) => import('../../../../../common/constants/filters/select-filter/types').SelectFilterMonad<'positionJobFamily'>;
	getInitialState: (
		config: CloudConfig,
	) => import('../../../../../common/constants/filters/select-filter/types').SelectFilterMonad<'positionJobFamily'>;
	isCompatible: (filterId?: string) => boolean;
	isSupportCustomValue: boolean;
	isSuggestedFilter?: (primaryProduct?: string) => boolean;
}> = createMultiSelectFilter({
	id: 'positionJobFamily',
	products: productKeys1P,
	alwaysVisible: false,
	universal: false,
	queryParams: {
		key: 'positionJobFamilies',
		fetcher: getOptionsWithSelectionsFromQueryParams,
	},
	initialOptions: {
		fetcher: async (config) => {
			return searchPositionJobFamilies('', config);
		},
	},
	lookup: {
		fetcher: searchPositionJobFamilies,
	},
});
